import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { toTrustedResourceUrl } from '../../../../../helpers/trusted-resource-url.helper';
import { ContentType } from '../../../../../shared/models/enums/content-type.enum';
import type { VideoContent, VimeoContent, YoutubeContent } from '../../../../../shared/models/interfaces/content.interface';
import type { ContentProgressUpdate } from '../../content-progress-update';
import type { ContentPositionUpdate } from '../../content-position-update';

interface YoutubePlayerInstance {
  destroy: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  pauseVideo: () => void;
}

interface VimeoPlaybackData {
  percent?: number;
  seconds?: number;
}

interface VimeoPlayerInstance {
  destroy: () => Promise<void> | void;
  on: (event: string, callback: (data: VimeoPlaybackData) => void) => void;
  getDuration: () => Promise<number>;
  setCurrentTime: (seconds: number) => Promise<number>;
}

const YOUTUBE_ENDED = 0;
const YOUTUBE_PLAYING = 1;
const PLAYBACK_POLL_MS = 1000;
const RESUME_END_TOLERANCE_S = 1;

let youtubeApiPromise: Promise<void> | null = null;
let vimeoApiPromise: Promise<void> | null = null;

@Component({
  selector: 'l-content-video',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './content-video.component.html',
})
export class ContentVideoComponent implements AfterViewChecked, OnInit, OnChanges, OnDestroy {

  @ViewChild('videoFrame')
  public videoFrame?: ElementRef<HTMLIFrameElement>;

  @ViewChild('nativeVideo')
  public nativeVideo?: ElementRef<HTMLVideoElement>;

  @Input()
  public content!: VideoContent | YoutubeContent | VimeoContent;

  @Input()
  public classId: string = '';

  /** Segundo do vídeo em que o aluno parou; nulo quando ainda não há posição salva. */
  @Input()
  public resumePosition: number | null = null;

  @Output()
  public progressChange: EventEmitter<ContentProgressUpdate> = new EventEmitter<ContentProgressUpdate>();

  @Output()
  public positionChange: EventEmitter<ContentPositionUpdate> = new EventEmitter<ContentPositionUpdate>();

  public readonly contentType = ContentType;

  private boundClassId: string = '';

  private lastReportedProgress: number = 0;

  private appliedEmbedUrl: string | null = null;

  private playbackSession: number = 0;

  private embeddedPlayerBound: boolean = false;

  private resumeApplied: boolean = false;

  private pollId: ReturnType<typeof globalThis.setInterval> | null = null;

  private youtubePlayer?: YoutubePlayerInstance;

  private vimeoPlayer?: VimeoPlayerInstance;

  public get nativeVideoSrc(): string {
    if (this.content?.type !== ContentType.VIDEO) {
      return '';
    }

    return (this.content.manifestPath || this.content.urlFolder || '').trim();
  }

  public get safeEmbeddedUrl(): string | null {
    if (!this.content) return null;

    const embedUrl = this.resolveEmbeddedUrl(this.content);

    if (!embedUrl) return null;

    return toTrustedResourceUrl(embedUrl, {
      allowSameOrigin: false,
      allowedHosts: ['www.youtube.com', 'player.vimeo.com'],
    });
  }

  public ngOnInit(): void {
    this.boundClassId = this.classId;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId']) {
      this.boundClassId = this.classId;
    }

    if (changes['content'] && !changes['content'].firstChange) {
      this.resetEmbeddedPlayer();
      this.lastReportedProgress = 0;
    }
  }

  public ngOnDestroy(): void {
    this.publishCurrentPlayback();
    this.resetEmbeddedPlayer();
  }

  public ngAfterViewChecked(): void {
    this.syncFrameSource();
  }

  public onNativeTimeUpdate(event: Event): void {
    const video = event.target as HTMLVideoElement | null;
    if (!video) return;

    this.reportPlayback(video.currentTime, video.duration);
  }

  public onNativeMetadataLoaded(event: Event): void {
    const video = event.target as HTMLVideoElement | null;
    if (!video) return;

    const start = this.resolveStartSeconds(video.duration);
    if (start > 0) {
      video.currentTime = start;
    }

    this.resumeApplied = true;
  }

  public onNativeEnded(): void {
    this.reportProgress(100);
  }

  public onEmbeddedFrameLoad(): void {
    const frame = this.videoFrame?.nativeElement;
    if (this.embeddedPlayerBound || !frame?.getAttribute('src')) return;

    const contentType = this.content?.type;
    if (contentType !== ContentType.YOUTUBE && contentType !== ContentType.VIMEO) return;

    this.embeddedPlayerBound = true;
    const session = ++this.playbackSession;

    void this.bindEmbeddedPlayer(frame, contentType, session);
  }

  private async bindEmbeddedPlayer(frame: HTMLIFrameElement, contentType: ContentType, session: number): Promise<void> {
    try {
      if (contentType === ContentType.YOUTUBE) {
        await loadYoutubeIframeApi();
        if (session !== this.playbackSession) return;
        this.bindYoutubePlayer(frame);
        return;
      }

      await loadVimeoPlayerApi();
      if (session !== this.playbackSession) return;
      this.bindVimeoPlayer(frame, session);
    } catch {
      this.embeddedPlayerBound = false;
    }
  }

  private bindYoutubePlayer(frame: HTMLIFrameElement): void {
    const youtube = youtubeHost().YT;
    if (!youtube?.Player) return;

    this.youtubePlayer = new youtube.Player(frame, {
      events: {
        onReady: () => this.applyYoutubeResume(),
        onStateChange: (event: { data: number }) => {
          if (event.data === YOUTUBE_PLAYING) {
            this.startPlaybackPolling();
            return;
          }

          this.publishCurrentPlayback();
          this.stopPlaybackPolling();

          if (event.data === YOUTUBE_ENDED) {
            this.reportProgress(100);
          }
        },
      },
    });
  }

  private applyYoutubeResume(): void {
    const player = this.youtubePlayer;
    if (!player) return;

    const start = this.resolveStartSeconds(player.getDuration());
    if (start > 0) {
      player.seekTo(start, true);
      player.pauseVideo();
    }

    this.resumeApplied = true;
  }

  private bindVimeoPlayer(frame: HTMLIFrameElement, session: number): void {
    const vimeo = vimeoHost().Vimeo;
    if (!vimeo?.Player) return;

    const player = new vimeo.Player(frame);
    this.vimeoPlayer = player;
    const onPlayback = (data: VimeoPlaybackData): void => {
      this.reportProgress((data.percent ?? 0) * 100);
      this.reportPosition(data.seconds ?? Number.NaN);
    };
    player.on('timeupdate', onPlayback);
    player.on('seeked', onPlayback);
    player.on('pause', onPlayback);
    player.on('ended', () => {
      this.reportProgress(100);
    });
    void this.applyVimeoResume(player, session);
  }

  private async applyVimeoResume(player: VimeoPlayerInstance, session: number): Promise<void> {
    try {
      const duration = await player.getDuration();
      if (session !== this.playbackSession) return;

      const start = this.resolveStartSeconds(duration);
      if (start > 0) {
        await player.setCurrentTime(start);
      }
    } catch {
      // Sem a API do player o vídeo começa do início.
    }

    if (session === this.playbackSession) {
      this.resumeApplied = true;
    }
  }

  /**
   * Segundo em que o player deve abrir: a posição salva. Uma parada no fim do vídeo recomeça do início.
   */
  private resolveStartSeconds(duration: number): number {
    const start = this.resumePosition ?? 0;

    return Number.isFinite(duration) && start > 0 && start < duration - RESUME_END_TOLERANCE_S ? start : 0;
  }

  private startPlaybackPolling(): void {
    this.stopPlaybackPolling();
    this.pollId = globalThis.setInterval(() => this.publishCurrentPlayback(), PLAYBACK_POLL_MS);
  }

  private stopPlaybackPolling(): void {
    if (this.pollId === null) return;

    globalThis.clearInterval(this.pollId);
    this.pollId = null;
  }

  private publishCurrentPlayback(): void {
    const video = this.nativeVideo?.nativeElement;
    if (video) {
      this.reportPlayback(video.currentTime, video.duration);
      return;
    }

    if (!this.youtubePlayer) return;

    this.reportPlayback(this.youtubePlayer.getCurrentTime(), this.youtubePlayer.getDuration());
  }

  private reportPlayback(currentTime: number, duration: number): void {
    if (!Number.isFinite(currentTime) || !Number.isFinite(duration) || duration <= 0) return;

    this.reportProgress((currentTime / duration) * 100);
    this.reportPosition(currentTime);
  }

  private reportPosition(currentTime: number): void {
    if (!this.resumeApplied || !Number.isFinite(currentTime) || currentTime < 0) return;

    this.positionChange.emit({
      classId: this.boundClassId,
      position: currentTime,
    });
  }

  private reportProgress(progress: number): void {
    const clamped = Math.min(100, Math.max(0, progress));
    const normalized = clamped >= 99.5 ? 100 : Math.round(clamped);

    if (normalized <= this.lastReportedProgress) return;

    this.lastReportedProgress = normalized;
    this.progressChange.emit({
      classId: this.boundClassId,
      progress: normalized,
    });
  }

  private resetEmbeddedPlayer(): void {
    this.playbackSession += 1;
    this.stopPlaybackPolling();
    this.embeddedPlayerBound = false;
    this.resumeApplied = false;
    this.appliedEmbedUrl = null;

    try {
      this.youtubePlayer?.destroy();
    } catch {
      this.youtubePlayer = undefined;
    }

    this.youtubePlayer = undefined;
    this.releaseVimeoPlayer();
  }

  private releaseVimeoPlayer(): void {
    const player = this.vimeoPlayer;
    this.vimeoPlayer = undefined;

    if (!player) {
      return;
    }

    try {
      const pendingDestroy = player.destroy();
      if (pendingDestroy instanceof Promise) {
        pendingDestroy.catch(() => undefined);
      }
    } catch {
      this.vimeoPlayer = undefined;
    }
  }

  private resolveEmbeddedUrl(content: VideoContent | YoutubeContent | VimeoContent): string {
    if (content.type === ContentType.YOUTUBE) {
      const youtubeId = this.extractYoutubeId(content.videoUrl);
      if (!youtubeId) return '';

      const origin = globalThis.window?.location?.origin || 'http://localhost';
      const params = new URLSearchParams({
        enablejsapi: '1',
        origin,
        rel: '0',
      });

      return `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`;
    }

    if (content.type === ContentType.VIMEO) {
      const vimeoId = this.extractVimeoId(content.videoUrl);
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : '';
    }

    return '';
  }

  private extractYoutubeId(url: string): string {
    const regex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{6,})/;
    const match = regex.exec(url);
    return match ? match[1] : '';
  }

  private extractVimeoId(url: string): string {
    const regex = /vimeo\.com\/(?:video\/)?(\d+)/;
    const match = regex.exec(url);
    return match ? match[1] : '';
  }

  private syncFrameSource(): void {
    const frame = this.videoFrame?.nativeElement;

    if (!frame) return;

    const nextUrl = this.safeEmbeddedUrl;
    const currentAttribute = frame.getAttribute('src');

    if (!nextUrl) {
      if (currentAttribute !== null) {
        this.resetEmbeddedPlayer();
        frame.removeAttribute('src');
      }

      return;
    }

    if (this.appliedEmbedUrl === nextUrl) return;

    this.resetEmbeddedPlayer();
    this.appliedEmbedUrl = nextUrl;
    frame.setAttribute('src', nextUrl);
  }
}

interface YoutubeWindow {
  YT?: {
    Player: new (
      element: HTMLIFrameElement,
      options: { events?: { onReady?: () => void; onStateChange?: (event: { data: number }) => void } },
    ) => YoutubePlayerInstance;
  };
  onYouTubeIframeAPIReady?: () => void;
}

interface VimeoWindow {
  Vimeo?: {
    Player: new (element: HTMLIFrameElement) => VimeoPlayerInstance;
  };
}

function youtubeHost(): YoutubeWindow {
  return globalThis as unknown as YoutubeWindow;
}

function vimeoHost(): VimeoWindow {
  return globalThis as unknown as VimeoWindow;
}

function loadYoutubeIframeApi(): Promise<void> {
  const win = youtubeHost();
  if (win.YT?.Player) return Promise.resolve();
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previous = win.onYouTubeIframeAPIReady;
    win.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => {
      youtubeApiPromise = null;
      reject(new Error('YouTube API failed to load'));
    };
    document.body.appendChild(script);
  });

  return youtubeApiPromise;
}

function loadVimeoPlayerApi(): Promise<void> {
  const win = vimeoHost();
  if (win.Vimeo?.Player) return Promise.resolve();
  if (vimeoApiPromise) return vimeoApiPromise;

  vimeoApiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      vimeoApiPromise = null;
      reject(new Error('Vimeo API failed to load'));
    };
    document.body.appendChild(script);
  });

  return vimeoApiPromise;
}
